/**
 * BFF Cart Client — storefront JavaScript that communicates with the BFF middleware.
 *
 * Loaded on cart page and mini-cart. Handles:
 *   - Cart enrichment (D365 pricing, inventory, Market Cash)
 *   - Pre-checkout validation
 *   - Market Cash application
 *   - Customer context (tier, personalization)
 *
 * Usage in Liquid:
 *   <script src="{{ 'bff-cart-client.js' | asset_url }}" defer></script>
 *   <script>
 *     document.addEventListener('DOMContentLoaded', () => {
 *       MercolaBFF.init({ cartId: '{{ cart.id }}' });
 *     });
 *   </script>
 */
(function () {
  'use strict';

  const BFF_BASE = window.__MERCOLA_BFF_URL || '/apps/bff';

  const MercolaBFF = {
    _cartId: null,
    _customerToken: null,
    _enrichedCart: null,

    /**
     * Initialize the BFF client.
     * @param {Object} opts - { cartId: string }
     */
    init(opts) {
      this._cartId = opts.cartId;
      this._customerToken = this._getCustomerToken();

      // Auto-enrich on load if on cart page
      if (document.querySelector('[data-cart-enrichment]')) {
        this.enrichCart();
      }

      // Listen for cart updates (Shopify section rendering API)
      document.addEventListener('cart:updated', () => this.enrichCart());

      console.log('[MercolaBFF] initialized', { cartId: this._cartId });
    },

    /**
     * Enrich cart with D365 pricing, inventory warnings, Market Cash.
     * Updates DOM elements with data attributes.
     */
    async enrichCart() {
      if (!this._cartId) return null;

      try {
        const res = await this._fetch('/api/cart/enrich', {
          method: 'POST',
          body: JSON.stringify({ cart_id: this._cartId }),
        });

        this._enrichedCart = res;
        this._renderEnrichment(res);
        return res;
      } catch (err) {
        console.warn('[MercolaBFF] Cart enrichment failed:', err.message);
        return null;
      }
    },

    /**
     * Validate cart before checkout redirect.
     * Returns { valid: boolean, errors: string[] }
     */
    async validateForCheckout() {
      if (!this._cartId) return { valid: true, errors: [] };

      try {
        return await this._fetch('/api/cart/validate', {
          method: 'POST',
          body: JSON.stringify({ cart_id: this._cartId }),
        });
      } catch (err) {
        console.warn('[MercolaBFF] Validation failed:', err.message);
        return { valid: true, errors: [] }; // Allow checkout on BFF failure
      }
    },

    /**
     * Apply Market Cash to cart.
     * @param {number} amountCents - Amount in cents to apply
     */
    async applyMarketCash(amountCents) {
      if (!this._cartId) return null;

      try {
        const res = await this._fetch('/api/cart/apply-mc', {
          method: 'POST',
          body: JSON.stringify({ cart_id: this._cartId, amount: amountCents }),
        });

        if (res.applied) {
          this._renderMarketCashApplied(res);
        }
        return res;
      } catch (err) {
        console.warn('[MercolaBFF] Market Cash apply failed:', err.message);
        return null;
      }
    },

    /**
     * Get customer context (tier, MC balance).
     */
    async getCustomerContext() {
      try {
        return await this._fetch('/api/customer/context');
      } catch {
        return null;
      }
    },

    /**
     * Get Market Cash balance for logged-in customer.
     */
    async getMarketCashBalance() {
      try {
        return await this._fetch('/api/customer/market-cash');
      } catch {
        return { balance: 0, available: false };
      }
    },

    // ─── DOM Rendering ──────────────────────────────

    _renderEnrichment(cart) {
      // Inventory warnings
      const warningContainer = document.querySelector('[data-inventory-warnings]');
      if (warningContainer && cart.inventory_warnings?.length) {
        warningContainer.innerHTML = cart.inventory_warnings
          .map(w => `<div class="cart-warning cart-warning--${w.type}">${w.message}</div>`)
          .join('');
        warningContainer.style.display = 'block';
      }

      // Market Cash balance
      const mcEl = document.querySelector('[data-market-cash-balance]');
      if (mcEl && cart.market_cash_balance) {
        mcEl.textContent = '$' + (cart.market_cash_balance / 100).toFixed(2);
        mcEl.closest('[data-market-cash-section]')?.classList.remove('hidden');
      }

      // Market Cash applicable
      const mcApply = document.querySelector('[data-market-cash-applicable]');
      if (mcApply && cart.market_cash_applicable) {
        mcApply.textContent = '$' + (cart.market_cash_applicable / 100).toFixed(2);
      }

      // Autoship savings
      const asEl = document.querySelector('[data-autoship-savings]');
      if (asEl && cart.autoship_discount) {
        asEl.textContent = '-$' + (cart.autoship_discount / 100).toFixed(2);
        asEl.closest('[data-autoship-section]')?.classList.remove('hidden');
      }

      // Shipping estimate
      const shipEl = document.querySelector('[data-shipping-estimate]');
      if (shipEl && cart.shipping_estimate) {
        const se = cart.shipping_estimate;
        shipEl.textContent = se.cost === 0 ? 'FREE' : '$' + (se.cost / 100).toFixed(2);
      }

      // Free shipping progress
      const freeShipBar = document.querySelector('[data-free-ship-progress]');
      if (freeShipBar && cart.shipping_estimate) {
        const pct = Math.min(100, (cart.subtotal / cart.shipping_estimate.free_shipping_threshold) * 100);
        freeShipBar.style.width = pct + '%';

        const remainEl = document.querySelector('[data-free-ship-remaining]');
        if (remainEl && pct < 100) {
          const remaining = (cart.shipping_estimate.free_shipping_threshold - cart.subtotal) / 100;
          remainEl.textContent = `Add $${remaining.toFixed(2)} for free shipping!`;
          remainEl.style.display = 'block';
        }
      }

      // Customer tier badge
      const tierEl = document.querySelector('[data-customer-tier]');
      if (tierEl && cart.tier) {
        tierEl.textContent = cart.tier.charAt(0).toUpperCase() + cart.tier.slice(1);
        tierEl.className = 'tier-badge tier-badge--' + cart.tier;
      }

      // Per-line-item enrichment
      for (const item of cart.line_items) {
        const lineEl = document.querySelector(`[data-line-sku="${item.sku}"]`);
        if (!lineEl) continue;

        // Low stock badge
        if (item.d365_available !== undefined && item.d365_available <= 10 && item.d365_available > 0) {
          const badge = lineEl.querySelector('[data-stock-badge]');
          if (badge) {
            badge.textContent = `Only ${item.d365_available} left`;
            badge.style.display = 'inline-block';
          }
        }

        // Autoship label
        if (item.is_autoship) {
          const asLabel = lineEl.querySelector('[data-autoship-label]');
          if (asLabel) {
            asLabel.textContent = `Autoship every ${item.autoship_frequency || '30'} days — Save ${item.autoship_discount_pct}%`;
            asLabel.style.display = 'block';
          }
        }
      }

      // Dispatch event for other scripts
      document.dispatchEvent(new CustomEvent('bff:cart:enriched', { detail: cart }));
    },

    _renderMarketCashApplied(result) {
      const mcStatus = document.querySelector('[data-market-cash-status]');
      if (mcStatus) {
        mcStatus.textContent = result.message;
        mcStatus.className = 'mc-status mc-status--success';
      }

      const mcBalance = document.querySelector('[data-market-cash-balance]');
      if (mcBalance) {
        mcBalance.textContent = '$' + (result.remaining_balance / 100).toFixed(2);
      }
    },

    // ─── Fetch Utility ──────────────────────────────

    async _fetch(path, opts = {}) {
      const headers = {
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
      };
      if (this._customerToken) {
        headers['Authorization'] = 'Bearer ' + this._customerToken;
      }

      const res = await fetch(BFF_BASE + path, { ...opts, headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || 'BFF request failed');
      }
      return res.json();
    },

    _getCustomerToken() {
      // Shopify stores customer access token in a cookie or meta tag
      const meta = document.querySelector('meta[name="customer-access-token"]');
      if (meta) return meta.getAttribute('content');

      // Fallback: check Shopify's customer object
      if (window.__st?.cid) return null; // Logged in but no storefront token
      return null;
    },
  };

  // Expose globally
  window.MercolaBFF = MercolaBFF;
})();
